/* oxlint-disable no-console */
// libraries
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(tz);

import { Options, RRule, Weekday } from "rrule";

// utilities
import { Time } from "../time";
import { pymod } from "../rrule";

/**
 * Limitations
 * - do not pass in more than one byminutes. for timezones with minute changes it can mess up the hour / day shifting
 *
 * Related Github issues:
 *  - https://github.com/jkbrzt/rrule/issues/621
 *  - https://github.com/jkbrzt/rrule/issues/610
 */
export function createRRule(
  opts: Partial<Options> & Pick<Options, "dtstart">,
  timeZone: string,
  options?: { debug?: boolean; noCache?: boolean },
) {
  const { debug } = options || {};

  /**
   * remove the ms from the time for consistency
   */
  const rule = new RRule(
    {
      ...opts,
      until: opts.until ? dayjs(opts.until).set("ms", 0).tz("utc").toDate() : undefined,
      dtstart: dayjs(opts.dtstart).set("ms", 0).tz("utc").toDate(),
    },
    options?.noCache,
  );

  const _opts = structuredClone(rule.options);
  /**
   * If the hours / minutes are passed in then set the dtStart to have those hours / mins so that the date shifting is correct
   */
  let dtstart = dayjs(opts?.dtstart);
  if (opts.byhour) {
    const hours = Array.isArray(opts.byhour) ? opts.byhour[0] : opts.byhour;
    dtstart = dtstart.tz(timeZone).set("h", hours);
  }

  if (opts.byminute) {
    const minutes = Array.isArray(opts.byminute) ? opts.byminute[0] : opts.byminute;
    dtstart = dtstart.tz(timeZone).set("m", minutes);
  }

  const utcStart = dtstart.tz("utc").set("ms", 0);
  const startLocal = dtstart.tz(timeZone).set("ms", 0);

  /**
   * Timezone shift
   * - if the timezone is +ve then the day shift will be positive. ex. +10 => Friday locally and Thursday UTC
   */
  const dtZ = startLocal.utcOffset();
  const shiftedTime = dayjs(startLocal.toDate().getTime() - dtZ * Time.mins).tz(timeZone);

  /**
   * Day indexing error is cancelled out with relative changes
   * - if dtDay is +ve then reverse that change in the rrule ( - dtDay)
   */
  const dtYear = shiftedTime.year() - startLocal.year();
  const dtMonth = shiftedTime.month() - startLocal.month() + dtYear * 12;
  const dtDay =
    shiftedTime.date() -
    startLocal.date() +
    dtMonth * (dtMonth >= 0 ? startLocal.daysInMonth() : shiftedTime.daysInMonth());
  // const dtDay = shiftedTime.date() - startLocal.date() + dtMonth * 31;

  // dtMr is the hour shift that thi minuit shift requires
  let dtMr = 0;

  debug &&
    console.log({
      note: "dX is relative to local time",
      dtDay,
      dtMonth,
      dtYear,
      dz: -Math.sign(dtZ),
      utc: utcStart.format(),
      local: startLocal.format(),
      shifted: shiftedTime.format(),
      opts: rule.options,
    });

  /**
   * Half hour timezone changes exist so we need to handle minute shifts
   *
   * - dtMr is the remainder of the minute shift for the hour shift
   * - if the minute shift would change the hour account for that in the hour shift
   */
  if (opts.byminute && dtZ) {
    _opts.byminute = _opts.byminute?.map((m) => pymod(m - (dtZ % 60), 60));

    if (rule.options.byminute.length === 1) {
      dtMr = Math.floor((rule.options.byminute[0] - (dtZ % 60)) / 60);
    }
    debug && console.log("bymins", rule.options.byminute, "=>", _opts.byminute, "::  +h", dtMr);
  }

  /**
   * Shift for flat timezone change
   *
   * - the hour shift the minutes change can result in isn't accounted for here.
   * - The dtZ value does have minutes in it but the starting point isn't included so its always less that the 60 so it will be modulo-ed away
   */
  if (opts.byhour && dtZ) {
    _opts.byhour = _opts.byhour?.map((h) => pymod(h - dtZ / 60 + dtMr, 24));
    debug && console.log("byhour", rule.options.byhour, "=>", _opts.byhour);
  }

  /**
   * Shift the byweekday
   *
   * - this in indexded to 0 so [0-6]
   * - js Date object, dayjs object, and byweekday have different starting days (Su, Mo).
   *   ignore this ond just use the dt and pymod to wrap.
   */
  if (_opts.byweekday && dtDay) {
    _opts.byweekday = _opts.byweekday?.map((d) => pymod(d + dtDay, 7));
    debug &&
      console.log(
        "changed byweekday",
        rule.options.byweekday?.map((x) => new Weekday(x).toString()),
        "=>",
        _opts?.byweekday?.map((x) => new Weekday(x).toString()),
        _opts?.byweekday,
      );
  }

  /**
   * Shift the yearday when set
   *
   * - this in net indexed to 0 but 1 [1-356]
   */
  if (_opts.byyearday && _opts.byyearday.length && dtDay) {
    _opts.byyearday = _opts.byyearday.map((d) => pymod(d - dtDay - 1, 365) + 1);
    debug && console.log("changed byyearday", rule.options.byyearday, "=>", _opts?.byyearday);
  }

  /**
   * Shift the bymonth, this will happen when the timezone specific date is close to the beginning or end of the month
   *
   * - this is net indexed to 0 but 1 [1-12]
   */
  if (_opts.bymonth && dtMonth) {
    _opts.bymonth = _opts.bymonth?.map((m) => pymod(m + dtMonth - 1, 12) + 1);
    debug && console.log("changed bymonth", rule.options.bymonth, "=>", _opts?.bymonth);
  }

  /**
   * Shift the monhtday when set
   *
   * - this in not indexed to 0 but 1 [1 - 28/30/31]
   */
  if (_opts.bymonthday && _opts.bymonthday.length && dtDay) {
    /*
     * Monthly recurrences can constrain a day of the month without constraining
     * the month itself. In that case rrule leaves bymonth null, so use the
     * start month only to determine the number of days available for wrapping.
     */
    const recurrenceMonth = _opts.bymonth?.[0];
    // -1 here because dayjs is 0 indexed and rrule spec is 1 indexed
    const monthChange =
      recurrenceMonth === undefined ? dtstart : dtstart.set("month", recurrenceMonth - 1);
    _opts.bymonthday = _opts.bymonthday.map((d) => pymod(d + dtDay, monthChange.daysInMonth()));
    debug && console.log("monthdayCounts", recurrenceMonth, monthChange.daysInMonth());
    debug &&
      console.log(
        "monthdayCounts",
        rule.options.bymonth?.map((x) => [x, dayjs().set("month", x).daysInMonth()]),
      );
    debug &&
      console.log(
        "monthdayCounts",
        _opts.bymonth?.map((x) => [x, dayjs().set("month", x).daysInMonth()]),
      );
    debug && console.log("changed bymonthday", rule.options.bymonthday, "=>", _opts?.bymonthday);
  }

  if (_opts.bysetpos) {
    debug && console.log("change bysetpos", rule.options.bysetpos, "=>", _opts?.bysetpos);
  }

  return new RRule(_opts, options?.noCache);
}
